import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Bot, FileText, LibraryBig, Loader2, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { useConfig } from 'hooks/use-config';
import { useIsOwner } from 'hooks/use-is-owner';
import { useGetAgentsByFolderId, useGetAllKB, useGetFolderList } from 'features/chat/hooks/use-kb';
import {
  EmbeddedStatus,
  IKnowledgeBaseItem,
  KBType,
} from 'features/chat/agent-chat-module/types/kb.service.type';
import { AddKbFileModal } from './components/add-kb-file-modal';
import { AddKbLinkModal } from './components/add-kb-link-modal';
import { AddKbQaModal } from './components/add-kb-qa-modal';
import { AddKbWriteupModal } from './components/add-kb-writeup-modal';
import { ConfigureFolderModal } from './components/configure-folder-modal';
import { KbDocumentPage } from './components/kb-document-page';
import { KbRowActionDropdown } from './components/kb-row-action-dropdown';
import {
  PAGE_SIZE,
  REEMBED_POLL_INTERVAL_MS,
  REEMBED_POLL_TIMEOUT_MS,
} from './constants';
import { formatDate, getEmbedBadgeVariant, getEmbedStatusLabel, getKbName, getKbTypeLabel, isExternalWriteup } from './utils';
import { ViewOnlyBadge } from 'components/blocks/view-only-badge';
import { VisibilityBadge } from 'components/blocks/visibility-field';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';

const KB_TABS = [KBType.FILE, KBType.TEXT, KBType.LINK, KBType.QNA] as const;

function isEmbedTerminalStatus(status?: EmbeddedStatus) {
  return status === EmbeddedStatus.COMPLETED || status === EmbeddedStatus.FAILED;
}

function TableSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

export default function KnowledgeBaseFolderPage() {
  const { t } = useTranslation();
  const { folderId = '' } = useParams();
  const { configurationSettings } = useConfig();
  const projectKey = configurationSettings?.blocksKey || '';
  const [activeTab, setActiveTab] = useState<KBType>(KBType.FILE);
  const [page, setPage] = useState(0);
  const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);
  const [selectedKb, setSelectedKb] = useState<IKnowledgeBaseItem | null>(null);
  const [embedPollKbId, setEmbedPollKbId] = useState<string | null>(null);
  const [embedPollDeadline, setEmbedPollDeadline] = useState<number | null>(null);
  const [embedPollSawInProgress, setEmbedPollSawInProgress] = useState(false);

  const { data: folderListData, isLoading: isFolderLoading } = useGetFolderList(projectKey, !!projectKey);
  const folder = useMemo(
    () => folderListData?.data?.folders?.find((item) => item._id === folderId),
    [folderId, folderListData?.data?.folders]
  );
  const canEdit = useIsOwner(folder);

  const isSpecificKbPolling = Boolean(embedPollKbId && embedPollDeadline && Date.now() < embedPollDeadline);

  const startEmbedPolling = (kbId: string) => {
    setEmbedPollSawInProgress(false);
    setEmbedPollKbId(kbId);
    setEmbedPollDeadline(Date.now() + REEMBED_POLL_TIMEOUT_MS);
    void kbQuery.refetch();
  };

  const stopEmbedPolling = () => {
    setEmbedPollKbId(null);
    setEmbedPollDeadline(null);
    setEmbedPollSawInProgress(false);
  };

  const kbQuery = useGetAllKB(
    {
      project_key: projectKey,
      folder_id: folderId,
      kb_type: activeTab,
      limit: PAGE_SIZE,
      offset: page,
    },
    !!projectKey && !!folderId,
    {
      refetchInterval: (query) => {
        if (isSpecificKbPolling) return REEMBED_POLL_INTERVAL_MS;
        const items = query.state.data?.data?.knowledge_bases;
        const hasInProgress = items?.some(
          (kb: IKnowledgeBaseItem) =>
            kb.embad_status === EmbeddedStatus.PENDING ||
            kb.embad_status === EmbeddedStatus.PROCESSING
        );
        return hasInProgress ? REEMBED_POLL_INTERVAL_MS : false;
      },
      refetchIntervalInBackground: true,
    }
  );

  const agentsQuery = useGetAgentsByFolderId(
    {
      project_key: projectKey,
      folder_id: folderId,
    },
    !!projectKey && !!folderId
  );
  const knowledgeBases = kbQuery.data?.data?.knowledge_bases || [];
  const totalCount = kbQuery.data?.data?.total_count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const hasInProgressEmbed = useMemo(
    () =>
      knowledgeBases.some(
        (item) =>
          item.embad_status === EmbeddedStatus.PENDING ||
          item.embad_status === EmbeddedStatus.PROCESSING
      ),
    [knowledgeBases]
  );

  const isEmbedPolling = hasInProgressEmbed || isSpecificKbPolling;

  const getItemStatus = (item: IKnowledgeBaseItem): EmbeddedStatus => {
    if (embedPollKbId === item.id && !embedPollSawInProgress) {
      return EmbeddedStatus.PENDING;
    }
    return (item.embad_status as EmbeddedStatus) || EmbeddedStatus.PENDING;
  };

  useEffect(() => {
    if (!embedPollKbId || !embedPollDeadline) return;

    if (Date.now() >= embedPollDeadline) {
      stopEmbedPolling();
      return;
    }

    const item = knowledgeBases.find((kb) => kb.id === embedPollKbId);
    if (!item) return;

    if (
      item.embad_status === EmbeddedStatus.PENDING ||
      item.embad_status === EmbeddedStatus.PROCESSING
    ) {
      setEmbedPollSawInProgress(true);
      return;
    }

    // Ignore cached pre-reembed "completed" until we have seen in-progress once.
    if (embedPollSawInProgress && isEmbedTerminalStatus(item.embad_status)) {
      stopEmbedPolling();
    }
  }, [knowledgeBases, embedPollKbId, embedPollDeadline, embedPollSawInProgress]);

  useEffect(() => {
    if (!embedPollDeadline) return;

    const remaining = embedPollDeadline - Date.now();
    if (remaining <= 0) {
      stopEmbedPolling();
      return;
    }

    const timer = setTimeout(() => {
      stopEmbedPolling();
    }, remaining);

    return () => clearTimeout(timer);
  }, [embedPollDeadline]);

  const handleTabChange = (value: string) => {
    setActiveTab(value as KBType);
    setPage(0);
    setSelectedKb(null);
    stopEmbedPolling();
  };

  const displayedKb = useMemo(() => {
    if (!selectedKb) return null;
    const found = knowledgeBases.find((kb) => kb.id === selectedKb.id);
    if (!found) return selectedKb;
    const effectiveStatus = getItemStatus(found);
    return {
      ...found,
      embad_status: effectiveStatus,
    };
  }, [selectedKb, knowledgeBases, embedPollKbId, embedPollSawInProgress]);

  if (displayedKb) {
    return (
      <ConfigurationLayout>
        <div className="flex h-[calc(100dvh-11rem)] w-full min-h-0 flex-col overflow-hidden">
          <KbDocumentPage
            kb={displayedKb}
            folderName={folder?.Name || undefined}
            canEdit={canEdit}
            onBack={() => setSelectedKb(null)}
            onReembedStarted={() => startEmbedPolling(displayedKb.id)}
          />
        </div>
      </ConfigurationLayout>
    );
  }

  const renderAddAction = () => {
    if (activeTab === KBType.FILE) {
      return <AddKbFileModal folderId={folderId} projectKey={projectKey} />;
    }

    if (activeTab === KBType.LINK) {
      return <AddKbLinkModal folderId={folderId} projectKey={projectKey} />;
    }

    if (activeTab === KBType.TEXT) {
      return <AddKbWriteupModal folderId={folderId} projectKey={projectKey} />;
    }

    return <AddKbQaModal folderId={folderId} projectKey={projectKey} />;
  };

  const emptyStateMessage =
    t('NO_KB_TYPE_FOUND_IN_FOLDER_YET', {
      type: getKbTypeLabel(activeTab).toLowerCase(),
    }) || `No ${getKbTypeLabel(activeTab).toLowerCase()} found in this folder yet.`;

  return (
    <ConfigurationLayout>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="grid flex-1 grid-cols-[2.75rem_minmax(0,1fr)] gap-x-4 gap-y-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 dark:bg-muted/50">
            <LibraryBig className="h-5 w-5 text-primary dark:text-foreground" />
          </div>
          <div className="flex min-w-0 items-center gap-2 self-center">
            <h1 className="min-w-0 truncate text-2xl font-bold leading-tight tracking-tight text-foreground">
              {isFolderLoading
                ? t('LOADING_FOLDER') || 'Loading folder...'
                : folder?.Name || t('KNOWLEDGE_FOLDER') || 'Knowledge Folder'}
            </h1>
            {!isFolderLoading ? (
              <VisibilityBadge
                value={folder?.Visibility ?? folder?.visibility}
                className="shrink-0"
              />
            ) : null}
          </div>
          <p className="col-start-2 max-w-3xl text-sm leading-7 text-muted-foreground">
            {folder?.Description ||
              t('KNOWLEDGE_FOLDER_DEFAULT_DESCRIPTION') ||
              'This folder stores the source knowledge available for retrieval.'}
          </p>
          <div className="col-start-2 flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className="gap-2 rounded-full border-primary/20 bg-primary/10 px-3.5 py-1.5 text-sm font-medium text-foreground dark:border-border dark:bg-muted/50"
            >
              <Bot className="h-4 w-4 text-primary dark:text-foreground" />
              <span>
                {t('ASSOCIATED_AGENTS_LABEL') || 'Associated Agents:'}{' '}
                {agentsQuery.data?.data?.total_count ?? 0}
              </span>
            </Badge>
          </div>
        </div>

        <div className="flex flex-col gap-3 md:items-end">
          {isFolderLoading ? (
            <div
              className="flex w-full shrink-0 flex-col gap-1.5 rounded-lg border border-border p-3 md:ml-auto md:max-w-[240px]"
              aria-hidden
            >
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-3 w-full" />
            </div>
          ) : canEdit ? (
            <Button
              variant="outline"
              className="w-full md:w-auto"
              onClick={() => setIsConfigureModalOpen(true)}
              disabled={!folder}
            >
              <Settings className="h-4 w-4" />
              {t('CONFIGURE') || 'Configure'}
            </Button>
          ) : (
            <ViewOnlyBadge
              className="max-w-[240px] md:ml-auto"
              description={
                t('YOU_ARE_NOT_THE_OWNER_OF_THIS_KNOWLEDGE_BASE') ||
                'You are not the owner of this knowledge base.'
              }
            />
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="gap-5 border-b pb-5 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <CardTitle>{t('DOCUMENTS') || 'Documents'}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('BROWSE_AND_MANAGE_FOLDER_CONTENT') ||
                'Browse and manage the content available in this folder.'}
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 md:w-auto md:items-end">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="flex h-auto flex-wrap justify-start gap-2 overflow-visible border-0 bg-transparent p-0 shadow-none md:justify-end">
                {KB_TABS.map((tab) => (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    className="min-w-[92px] rounded-md border bg-card px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    {getKbTypeLabel(tab)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="flex justify-start md:justify-end">
              {canEdit ? renderAddAction() : null}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-0">
          {kbQuery.isLoading ? (
            <div className="p-6">
              <TableSkeleton />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto px-4 sm:px-6">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="border-b hover:bg-transparent">
                      <TableHead className="h-11 w-[42%] text-xs font-semibold uppercase tracking-wide text-foreground/80 dark:text-white/80">
                        {t('NAME') || 'Name'}
                      </TableHead>
                      {activeTab === KBType.FILE && (
                        <TableHead className="h-11 w-[12%] text-xs font-semibold uppercase tracking-wide text-foreground/80 dark:text-white/80">
                          {t('FILE_TYPE') || 'File Type'}
                        </TableHead>
                      )}
                      <TableHead className="h-11 w-[16%] text-xs font-semibold uppercase tracking-wide text-foreground/80 dark:text-white/80">
                        {t('STATUS') || 'Status'}
                      </TableHead>
                      <TableHead className="h-11 w-[10%] text-xs font-semibold uppercase tracking-wide text-foreground/80 dark:text-white/80">
                        {t('CHUNKS') || 'Chunks'}
                      </TableHead>
                      <TableHead className="h-11 w-[16%] text-xs font-semibold uppercase tracking-wide text-foreground/80 dark:text-white/80">
                        {t('LAST_UPDATED') || 'Last Updated'}
                      </TableHead>
                      <TableHead className="h-11 w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {knowledgeBases.length > 0 ? (
                      knowledgeBases.map((item) => {
                        const itemStatus = getItemStatus(item);
                        const isClickable = itemStatus === EmbeddedStatus.COMPLETED;

                        return (
                          <TableRow key={item.id} className="group hover:bg-muted/30">
                            <TableCell className="w-[42%] font-medium">
                              {isExternalWriteup(item) && <Badge variant="outline">{item.meta_data.ExternalSource?.label || item.meta_data.ExternalSource?.system}</Badge>}
                              {isClickable ? (
                                <button
                                  type="button"
                                  onClick={() => setSelectedKb(item)}
                                  title={getKbName(item)}
                                  className="block w-full min-w-0 overflow-hidden break-words text-left text-foreground transition-colors"
                                >
                                  <span className="line-clamp-2 block">{getKbName(item)}</span>
                                </button>
                              ) : (
                                <span
                                  title={getKbName(item)}
                                  className="block w-full min-w-0 overflow-hidden break-words text-foreground"
                                >
                                  <span className="line-clamp-2 block">{getKbName(item)}</span>
                                </span>
                              )}
                            </TableCell>
                            {activeTab === KBType.FILE && (
                              <TableCell className="text-muted-foreground">
                                {item.file?.name?.split('.').pop()?.toUpperCase() || '-'}
                              </TableCell>
                            )}
                            <TableCell>
                              <Badge
                                variant={getEmbedBadgeVariant(itemStatus)}
                                className={
                                  itemStatus === EmbeddedStatus.PROCESSING ||
                                    itemStatus === EmbeddedStatus.PENDING
                                    ? 'inline-flex items-center gap-1.5'
                                    : undefined
                                }
                              >
                                {(itemStatus === EmbeddedStatus.PROCESSING ||
                                  itemStatus === EmbeddedStatus.PENDING) && (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  )}
                                {getEmbedStatusLabel(itemStatus, t)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {item.total_chunks ?? 0}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(item.last_updated_date)}
                            </TableCell>
                            <TableCell>
                              {canEdit && !isExternalWriteup(item) ? (
                                <div
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                  }}
                                >
                                  <KbRowActionDropdown
                                    documentId={item.id}
                                    documentName={getKbName(item)}
                                    kbType={activeTab}
                                    projectKey={projectKey}
                                    onReembedStarted={() => startEmbedPolling(item.id)}
                                  />
                                </div>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={activeTab === KBType.FILE ? 6 : 5}>
                          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted dark:bg-white/10">
                              <FileText className="h-6 w-6 text-muted-foreground dark:text-white/80" />
                            </div>
                            <p className="max-w-md text-sm text-muted-foreground dark:text-white/75">
                              {emptyStateMessage}
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {totalCount > PAGE_SIZE && (
                <div className="flex flex-col gap-3 border-t px-4 pb-4 pt-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    {(t('PAGE_OF', { page: page + 1, total: totalPages }) ||
                      `Page ${page + 1} of ${totalPages}`)}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((current) => Math.max(0, current - 1))}
                      disabled={page === 0}
                    >
                      {t('PREVIOUS') || 'Previous'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      {t('NEXT') || 'Next'}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {folder && (
        <ConfigureFolderModal
          projectKey={projectKey}
          open={isConfigureModalOpen}
          onOpenChange={setIsConfigureModalOpen}
          folder={folder}
        />
      )}
    </ConfigurationLayout>
  );
}
